/**
 * Knowledge Base v2 — the shapes the backend sends for definitions learned
 * in a chat and definitions used in an answer.
 *
 * Field names match the wire (snake_case) so stream events, history and API
 * replies need no translation. See docs/features/knowledge/kb-v2-contract.md.
 */

/** Where a saved definition came from. */
export type DefinitionType = 'dashboard' | 'admin' | 'chat';

export type LearnedItemState = 'pending' | 'saved' | 'not_saved' | 'deleted';

export type LearnedVerdict =
  | 'new'
  | 'same_as_official'
  | 'differs_from_official'
  | 'same_as_saved'
  | 'differs_from_saved'
  | 'variant'
  | 'one_off';

export interface LearnedMatch {
  node_id: string;
  name: string;
  definition?: string;
  definition_type?: DefinitionType;
  board?: string;
}

export interface LearnedPerson {
  user_id?: string;
  name: string;
}

/** One definition the user taught the chat, as the card shows it. */
export interface LearnedItem {
  item_id: string;
  name: string;
  definition: string;
  knowledge_type?: 'metric' | 'business_rule' | 'term' | string;
  sql?: string;
  state: LearnedItemState;
  node_id?: string | null;
  check?: {
    verdict: LearnedVerdict;
    note?: string;
    match?: LearnedMatch | null;
  } | null;
  saved_by?: LearnedPerson | null;
  saved_at?: string | null;
  updated_by?: LearnedPerson | null;
  updated_at?: string | null;
}

/** One definition an answer relied on. */
export interface DefinitionUsed {
  node_id?: string | null;
  name: string;
  definition_type: DefinitionType | 'this_chat';
  board?: string | null;
  /**
   * How a measure was applied, decided from what ran: `dashboard` — the
   * answer quotes the dashboard's own figure; `own_query` — Copilot worked
   * the figure out itself, so it may differ from the dashboard.
   */
  applied?: 'dashboard' | 'own_query' | null;
}

/** Keep only well-formed learned items from an untrusted payload. */
export function toLearnedItems(raw: unknown): LearnedItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (i): i is LearnedItem =>
      !!i && typeof i === 'object' && typeof (i as LearnedItem).item_id === 'string' && typeof (i as LearnedItem).name === 'string',
  ).map((i) => ({ ...i, definition: i.definition ?? '', state: i.state ?? 'pending' }));
}

/** Keep only well-formed "definitions used" entries from an untrusted payload. */
export function toDefinitionsUsed(raw: unknown): DefinitionUsed[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (i): i is DefinitionUsed => !!i && typeof i === 'object' && typeof (i as DefinitionUsed).name === 'string' && !!(i as DefinitionUsed).name,
  );
}
